import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'react-daisyui';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import PageContainer from '@/components/PageContainer';
import { DashboardWrapper } from '@/components/DashboardWrapper';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { GenericModal } from '@/components/Modal';
import { Auth, IUserContext, useUserContext } from '@/context/UserContext';
import { InvestorReportDTO, InvestorReportTemplateDTO, Client } from '@types';
import { get } from '@/utils/queries';
import { LoadingPage } from '@/pages/LoadingPage';
import ErrorAlert from '@/components/ErrorAlert/ErrorAlert';
import { setInvestorReportSignal } from '@/pages/InvestorReporting/utils/investorReportDataSignal';
import { InvestorReportingManagement } from '@/pages/InvestorReporting/components/InvestorReportingManagement';
import RequestBuilder from './components/modals/RequestBuilder';

const templateURL = (
  auth: Auth,
  impersonatingAccountId: number | null
): string => {
  const basePath = 'investorReports/templates';

  if (auth.isInvestor) return basePath;

  if (auth.isClient) {
    const clientId = auth.clients?.[0]?.id;
    if (!clientId) {
      throw new Error('Client ID is missing for client user');
    }
    return `${basePath}/client/${clientId}`;
  }

  if (auth.isAdmin) {
    if (!impersonatingAccountId) {
      throw new Error('Impersonating account ID is required for admin');
    }
    return `${basePath}/client/${impersonatingAccountId}`;
  }

  return basePath;
};

interface Props {
  removeImpersonatingAccountIdForInvestors?: boolean;
}

function InvestorReporting({
  removeImpersonatingAccountIdForInvestors,
}: Props) {
  const [showRequestBuilderModal, setShowRequestBuilderModal] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientNameFromQuery = queryParams.get('clientName') || '';
  const showPastUpdates = queryParams.get('showPastUpdates');

  queryParams.delete('clientName');
  queryParams.delete('showPastUpdates');
  const newUrl = `${window.location.origin}/i-reporting`;
  window.history.replaceState({}, '', newUrl);

  const [currentView, setCurrentView] = useState<
    'activeRequests' | 'pastUpdates'
  >(clientNameFromQuery || showPastUpdates ? 'pastUpdates' : 'activeRequests');

  const {
    auth,
    companyName,
    impersonatingAccountId,
    setimpersonatingAccountId,
  }: IUserContext = useUserContext();

  useEffect(() => {
    if (
      removeImpersonatingAccountIdForInvestors &&
      setimpersonatingAccountId &&
      auth?.isInvestor
    ) {
      setimpersonatingAccountId(null);
    }
  }, [
    removeImpersonatingAccountIdForInvestors,
    setimpersonatingAccountId,
    auth?.isInvestor,
  ]);

  const {
    data: investorReports,
    isValidating: isLoadingReports,
    error: reportsError,
    mutate: mutateReports,
  } = get<InvestorReportDTO[]>(
    impersonatingAccountId
      ? `investorReports?clientId=${impersonatingAccountId}`
      : 'investorReports'
  );

  const {
    data: allInvestorReports,
    isValidating: isLoadingAllReports,
    error: allReportsError,
  } = get<InvestorReportDTO[]>(
    impersonatingAccountId
      ? `investorReports/all?clientId=${impersonatingAccountId}`
      : 'investorReports/all'
  );

  const {
    data: investorReportTemplates,
    isValidating: isLoadingReportTemplates,
    error: reportTemplatesError,
    mutate: mutateReportTemplates,
  } = get<InvestorReportTemplateDTO[]>(
    templateURL(auth as Auth, impersonatingAccountId)
  );

  const { data: companyData } = auth?.isClient
    ? get<Client[]>(`account/${auth.clients[0].id}`)
    : get<Client[]>('account/list');

  const toggleView = () => {
    setCurrentView((prevView) =>
      prevView === 'activeRequests' ? 'pastUpdates' : 'activeRequests'
    );
  };

  if (
    (!investorReports && isLoadingReports) ||
    (!allInvestorReports && isLoadingAllReports) ||
    (!investorReportTemplates && isLoadingReportTemplates)
  ) {
    return <LoadingPage hasNav />;
  }

  setInvestorReportSignal({
    reportData: investorReports,
    isReportsLoading: isLoadingReports,
    reportsError,
    mutateReports,
  });

  if (reportsError || allReportsError || reportTemplatesError) {
    return (
      <PageContainer name='client-list'>
        <DashboardWrapper>
          <ErrorAlert
            isSuperAdmin={auth?.isSuperAdmin}
            error={
              (reportsError ||
                allReportsError ||
                reportTemplatesError) as unknown
            }
            context='InvestorReporting'
          />
        </DashboardWrapper>
      </PageContainer>
    );
  }

  const props = {
    ...(companyName && { companyName }),
    ...(companyData && { companyData }),
  };

  return (
    <PageContainer name='investor-account'>
      <DashboardWrapper {...props}>
        <div className='p-4'>
          <div className='flex w-full justify-between items-center mb-4'>
            <h1 className='text-lg font-bold uppercase'>
              <FontAwesomeIcon
                icon={faChartLine}
                size='lg'
                className='mr-4 text-white'
              />
              {auth?.isInvestor ? 'Report Builder' : 'Investor Reporting'}
            </h1>
            <div className='text-lg font-bold uppercase text-right flex'>
              {auth?.isInvestor ? (
                <Button
                  color='accent'
                  size='sm'
                  onClick={() => {
                    setShowRequestBuilderModal(true);
                  }}
                  className='mr-2'
                >
                  Request builder
                </Button>
              ) : null}
              <Button color='accent' size='sm' onClick={toggleView}>
                {currentView === 'activeRequests'
                  ? 'Past Updates'
                  : 'Active Requests'}
              </Button>
            </div>
          </div>
        </div>

        {auth?.isInvestor && showRequestBuilderModal ? (
          <GenericModal
            isOpen={showRequestBuilderModal}
            onClose={() => setShowRequestBuilderModal(false)}
            className='max-w-[35%]'
          >
            <RequestBuilder
              onSave={async () => {
                setShowRequestBuilderModal(false);
                await mutateReports();
              }}
            />
          </GenericModal>
        ) : null}
        <InvestorReportingManagement
          investorReports={investorReports || []}
          allInvestorReports={allInvestorReports || []}
          user={auth}
          updateData={async () => {
            await mutateReports();
            await mutateReportTemplates();
          }}
          investorReportTemplates={investorReportTemplates || []}
          impersonatingAccountId={impersonatingAccountId}
          currentView={currentView}
        />
      </DashboardWrapper>
    </PageContainer>
  );
}

export default InvestorReporting;
